import { TRootState } from '../../common/reducers';
import { IGenerateAnswerState, PresentedState } from '../../common/reducers/types';
import { selectCurrentPresentedState, selectPreviousPresentedState } from '../../common/selectors/helpCenter';
import { selectHomepageContent, selectRecommendedArticles } from '../../common/selectors/homepage';
import { selectSolutions } from '../../common/selectors/solutions';
import { biLogger } from '../biLogger';
import { locale } from '../constants';
import { selectBiData } from '../selectors';
import {
  createArticleSolutionInfo,
  createCategoriesSolutionInfo,
  createHomepageSolutionInfo,
  createSearchResultsSolutionInfo,
  createSolutionListSolutionInfo,
} from '../solutionInfo';
import { ArticleBiData, BiEventLogger, CategoryBiData, SolutionInfo } from '../types';
import { getKbWidgetState, getRuntimeDataForBi } from '../utils';
import { getSumarizedAnswersGenFieldsBi } from './logWidgetContentInteraction';

export const logHomepagePresented: BiEventLogger = (state) => {
  const content = selectHomepageContent(state);
  const recommendedArticles = selectRecommendedArticles(state);

  void widgetPresentedContent({
    state,
    solutionInfo: createHomepageSolutionInfo(content.data, recommendedArticles.data?.articles),
  });
};

export const logCategoriesPresented: BiEventLogger<CategoryBiData[]> = (state, categories) => {
  const presentedState = selectCurrentPresentedState(state);
  const isSubCategory = presentedState === PresentedState.SubCategoriesList;

  void widgetPresentedContent({
    state,
    solutionInfo: createCategoriesSolutionInfo(categories, isSubCategory),
  });
};

export const logCategorySolutionListPresented: BiEventLogger<ArticleBiData[]> = (state, articles) => {
  void widgetPresentedContent({
    state,
    solutionInfo: createSolutionListSolutionInfo(articles),
  });
};

export const logSingleArticlePresented: BiEventLogger<ArticleBiData> = (state, article) => {
  const previousPresentedState = selectPreviousPresentedState(state);

  void widgetPresentedContent({
    state,
    solutionInfo: createArticleSolutionInfo({ articleId: article.id, articleType: article.articleType }),
    isChatbotSolutionClick: previousPresentedState === PresentedState.Chatbot,
    lang: article.locale,
  });
};

export const logSearchResultsPresented: BiEventLogger<{
  articles: ArticleBiData[];
  generatedAnswer?: IGenerateAnswerState;
}> = (state, { articles, generatedAnswer }) => {
  void widgetPresentedContent({
    state,
    solutionInfo: createSearchResultsSolutionInfo(articles),
    generatedAnswer,
  });
};

async function widgetPresentedContent({
  state,
  solutionInfo,
  isChatbotSolutionClick = false,
  lang = locale,
  generatedAnswer,
}: {
  state: TRootState;
  solutionInfo: SolutionInfo | SolutionInfo[];
  isChatbotSolutionClick?: boolean;
  lang?: string;
  generatedAnswer?: IGenerateAnswerState;
}): Promise<void> {
  const biData = selectBiData(state);
  const { userPriorityClass, sessionId, metaSiteId, contextBi, placement } = await getRuntimeDataForBi(state);

  const solutions = selectSolutions(state);

  biLogger.widgetPresentedContent({
    article_id: biData.articleId,
    article_recommendation_version: biData.articleRecommendationVersion,
    category_id: biData.categoryId,
    ticket_id: biData.ticketId,
    last_conversation_id: biData.conversationId,
    last_chatbot_node_id: biData.nodeId,
    last_user_intent_id: biData.intentId,
    current_presented_state: biData.currentPresentedState,
    previous_presented_state: biData.previousPresentedState,
    searched_text: biData.searchedText,
    orig_page_name: biData.origPageName,
    orig_platform: biData.origPlatform,
    solution_info: JSON.stringify(solutionInfo),
    is_chatbot_solution_click: isChatbotSolutionClick,
    kb_widget_state: getKbWidgetState(state),
    placement,
    current_page_name: document.title,
    chatbot_view: contextBi?.chatbot_view,
    session_id: sessionId,
    priority_class: userPriorityClass,
    lng: locale,
    lang,
    msid: metaSiteId,
    context_area: contextBi?.context_area,
    context_domain: contextBi?.context_domain,
    context_iframeurllist: contextBi?.context_iframeurllist,
    context_msid: contextBi?.context_msid,
    context_page_numlevels: contextBi?.context_page_numlevels,
    context_page_queryparams: contextBi?.context_page_queryparams,
    context_page_toplevelroute: contextBi?.context_page_toplevelroute,
    context_page_urlchunks: contextBi?.context_page_urlchunks,
    context_parenturl: contextBi?.context_parenturl,
    context_platform: contextBi?.context_platform,
    context_uuid: contextBi?.context_uuid,
    user_experience: contextBi?.user_experience,
    last_user_intent: undefined,
    suggestion_id: undefined,
    widget_state: undefined,
    widget_view_mode: undefined,
    referral_info: undefined,
    last_chatbot_node_name: undefined,
    chatbot_state: undefined,
    summarized_answer_words_count: undefined,
    summarized_answer_type: undefined,
    summarized_answer_text: undefined,
    summarized_answer_based_on: undefined,
    is_support_request: undefined,
    context_editor_type: undefined,
    ...(generatedAnswer && getSumarizedAnswersGenFieldsBi(solutions || [], generatedAnswer)),
  });
}
